import React, { useRef, useState } from 'react'

import type { IconType } from 'react-icons'
import { HiOutlineComputerDesktop, HiOutlinePaperClip } from 'react-icons/hi2'

import Attachment, {
  AttachmentBase,
  type Attachment as AttachmentProps,
} from '@/components/common/Attachment'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import IconButton from '@/components/common/IconButton'

import { ALLOWED_MIME_TYPES } from '@/constants/file'

export interface ClickableAttachment extends AttachmentProps {
  onClick: () => void
}

export interface AttachmentGroup {
  label: string
  icon: IconType
  attachments: ClickableAttachment[]
}

interface Attachments {
  attachments: AttachmentProps[]
}

interface AttachmentMenu {
  attachmentGroups: AttachmentGroup[]
  onAddLocal?: (files: File[]) => void
}

export const AttachmentMenu: React.FC<AttachmentMenu> = ({
  attachmentGroups,
  onAddLocal,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const inputRef = useRef<HTMLInputElement>(null)

  const handleFocusOutside = (event: Event) => {
    // https://github.com/radix-ui/primitives/issues/2565;
    // https://github.com/radix-ui/primitives/issues/2348;
    // prevents the dropdown from immediately closing when clicking on the button
    event.preventDefault()
  }

  const handleAddLocal = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && onAddLocal) {
      onAddLocal(Array.from(files))
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        id="file-input"
        className="hidden"
        multiple={true}
        accept={ALLOWED_MIME_TYPES.join(',')}
        // always set as an empty array so that we don't need to manage state
        // in this component and instead leave it up to the parent
        value={[]}
        onChange={handleAddLocal}
      />
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
        <DropdownMenuTrigger asChild={true}>
          <IconButton>
            <HiOutlinePaperClip className="w-4 h-4 text-primary-700 dark:text-primary-300" />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-60"
          onFocusOutside={handleFocusOutside}
          side="top"
          align="start"
        >
          <DropdownMenuGroup>
            {onAddLocal && (
              <DropdownMenuItem onClick={() => inputRef.current?.click()}>
                <HiOutlineComputerDesktop className="flex-shrink-0 w-4 h-4 mr-2" />
                <span className="whitespace-nowrap">
                  Upload from your computer
                </span>
              </DropdownMenuItem>
            )}
            {attachmentGroups.map((group, idx) => {
              return (
                <DropdownMenuSub key={idx}>
                  <DropdownMenuSubTrigger>
                    <group.icon className="flex-shrink-0 w-4 h-4 mr-2" />
                    {group.label}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      {group.attachments.map((attachment, idx) => {
                        return (
                          <DropdownMenuItem
                            key={idx}
                            onClick={attachment.onClick}
                            className="px-[6px] max-w-96"
                          >
                            <AttachmentBase
                              label={attachment.label}
                              description={attachment.description}
                              thumbnail={attachment.thumbnail}
                              isLoading={attachment.isLoading}
                            />
                          </DropdownMenuItem>
                        )
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )
            })}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

export const Attachments: React.FC<Attachments> = ({ attachments }) => {
  if (!attachments || attachments.length === 0) {
    return null
  }
  return (
    <div className="flex flex-wrap gap-2 px-[6px] pb-1">
      {attachments.map((a, idx) => (
        <Attachment key={idx} {...a} className="max-w-60" />
      ))}
    </div>
  )
}
